import { z } from 'zod';
import { extendZodWithOpenApi } from '@asteasolutions/zod-to-openapi';
extendZodWithOpenApi(z);
// ─── Request Schemas ────────────────────────────────────────────────────────
export const GenerateContentRequestSchema = z
    .object({
    scope: z.enum(['course', 'module', 'topic', 'lesson']).openapi({
        description: 'Scope of content to generate',
        example: 'course',
    }),
    parentId: z.string().uuid().optional().openapi({
        description: 'Parent entity ID (required for non-course scopes). Module ID for topic scope, etc.',
        example: '550e8400-e29b-41d4-a716-446655440000',
    }),
    prompt: z.string().min(10).max(2000).openapi({
        description: 'Natural language description of the content to generate',
        example: 'Create a beginner Python programming course covering variables, loops, and functions',
    }),
    /** @deprecated Use courseMapContext and resourceContexts instead */
    fileContexts: z
        .array(z.object({
        filename: z.string(),
        text: z.string(),
    }))
        .max(10)
        .optional()
        .openapi({ description: 'Optional file contents to provide context for generation' }),
    courseMapContext: z
        .object({
        filename: z.string(),
        text: z.string(),
    })
        .optional()
        .openapi({
        description: 'Course map / curriculum / syllabus — structural guidance for the AI (single file)',
    }),
    resourceContexts: z
        .array(z.object({
        filename: z.string(),
        text: z.string(),
    }))
        .max(10)
        .optional()
        .openapi({
        description: 'Resource files containing source material for the AI to teach from',
    }),
    settings: z
        .object({
        progressionType: z.enum(['lesson', 'topic', 'module']).optional().openapi({
            description: 'Type of progression tracking',
            example: 'lesson',
        }),
        language: z.string().optional().openapi({
            description: 'Language code for generated content',
            example: 'en',
        }),
        excludedStepTypes: z
            .array(z.string())
            .optional()
            .openapi({
            description: 'Step types to exclude from generation',
            example: ['true_false'],
        }),
        contextDepth: z.enum(['sibling', 'full']).default('sibling').openapi({
            description: 'How much existing context to load for non-course scopes: sibling (adjacent items) or full (entire course)',
            example: 'sibling',
        }),
    })
        .optional()
        .openapi({ description: 'Generation settings' }),
    editorIds: z
        .array(z.string().uuid())
        .max(20)
        .optional()
        .default([])
        .openapi({ description: 'Optional user IDs to add as course editors after generation' }),
    devFlags: z
        .object({
        skipDependencyCheck: z.boolean().optional().openapi({
            description: 'Skip post-generation dependency check',
        }),
        skipQualityCheck: z.boolean().optional().openapi({
            description: 'Skip post-generation quality check',
        }),
        singleLessonMode: z.boolean().optional().openapi({
            description: 'Generate only a single lesson for testing',
        }),
    })
        .optional()
        .openapi({ description: 'Development/debug flags' }),
})
    .openapi('GenerateContentRequest');
// ─── Parameter Schemas ──────────────────────────────────────────────────────
export const GenerationJobParamSchema = z.object({
    jobId: z.string().uuid().openapi({
        description: 'Background generation job UUID',
        example: '550e8400-e29b-41d4-a716-446655440000',
    }),
});
// ─── Body Schemas ───────────────────────────────────────────────────────────
export const RefineGenerationSchema = z
    .object({
    feedback: z
        .string()
        .min(1, 'Feedback is required')
        .max(5000, 'Feedback must be less than 5000 characters')
        .trim()
        .openapi({
        description: 'Feedback describing desired changes to the proposed structure',
        example: 'Add more exercises and reduce the number of info steps',
    }),
})
    .openapi('RefineGeneration');
// ─── Query Schemas ──────────────────────────────────────────────────────────
export const ExportQuerySchema = z
    .object({
    format: z.enum(['json', 'csv']).optional().default('json').openapi({
        description: 'Export output format',
        example: 'json',
    }),
})
    .openapi('ExportQuery');
//# sourceMappingURL=generation.schema.js.map